import React, { useEffect, useState } from "react";
import api, { resolveImageUrl } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

const EMPTY = () => ({ slug: "", name: "", description: "", image: "", parent: "Hardware", is_active: true, order: 0 });

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/admin/categories/${id}`);
    toast.success("Category deleted");
    load();
  };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/categories/${editing.id}`, editing);
      else await api.post(`/admin/categories`, editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-2">Shop</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Manage Categories</h1>
        </div>
        <button onClick={() => setEditing(EMPTY())} className="btn-msr-primary" data-testid="admin-categories-add-btn">
          <Plus size={14} /> Add Category
        </button>
      </div>

      <div className="bg-white border border-msr-line">
        <table className="w-full text-sm">
          <thead className="bg-msr-mist text-left text-[11px] uppercase tracking-wider text-msr-slate">
            <tr>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Parent</th>
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-msr-line" data-testid={`admin-category-row-${c.slug}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 bg-msr-mist overflow-hidden">
                      {c.image && <img src={resolveImageUrl(c.image)} className="h-full w-full object-cover" alt="" />}
                    </div>
                    <div>
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-msr-slate">{c.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-msr-slate">{c.parent}</td>
                <td className="px-4 py-3">{c.order}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...EMPTY(), ...c })} className="text-msr-blue inline-flex items-center gap-1 text-xs mr-3" data-testid={`admin-category-edit-${c.slug}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button onClick={() => remove(c.id)} className="text-red-500 inline-flex items-center gap-1 text-xs" data-testid={`admin-category-delete-${c.slug}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 bg-black/60 grid place-items-center p-4" data-testid="admin-category-editor">
          <div className="bg-white w-full max-w-2xl border-t-4 border-msr-blue">
            <div className="border-b border-msr-line px-6 py-4 flex justify-between">
              <div className="font-heading font-bold">Category</div>
              <button onClick={() => setEditing(null)} className="p-2"><X size={20} /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <Field label="Slug *" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testId="cat-edit-slug" />
              <Field label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="cat-edit-name" />
              <Select label="Parent" value={editing.parent} onChange={(v) => setEditing({ ...editing, parent: v })} options={["Hardware", "Software", "Service", "Bundle"]} testId="cat-edit-parent" />
              <Field label="Order" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: Number(v) })} testId="cat-edit-order" />
              <div className="col-span-2"><Field label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testId="cat-edit-desc" /></div>
              <div className="col-span-2"><ImageUploader label="Category image" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} testId="cat-edit-image" /></div>
              <div className="col-span-2 flex items-center justify-end gap-2 pt-4 border-t border-msr-line">
                <button onClick={() => setEditing(null)} className="text-sm px-4 py-2 border border-msr-line">Cancel</button>
                <button onClick={save} className="btn-msr-primary py-2 px-4 text-xs" data-testid="admin-category-save-btn">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue" data-testid={testId} />
    </label>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line px-3 py-2.5 text-sm focus:outline-none focus:border-msr-blue" data-testid={testId}>
        {options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </label>
  );
}
